// @flow
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';
import {ofType} from 'redux-observable';
import {debounceTime, map, switchMap, mergeMap} from 'rxjs/operators';
import {of} from 'rxjs';
import {
  deleteCardApi,
  fetchCardApi,
  fetchCardsApi,
  performSubscribeApi,
  endpoints,
  freeTrialSubscribeApi,
  getPaymentProvidersApi,
  fetchPurchaseSummaryApi,
  payUInitializeApi,
  stripeInitializeApi,
  stripeUpdateApi,
  stripeAddCardInitializeApi,
  fetchCardStateApi,
  setCurrentCardApi,
} from './api';
import type {PaymentProvider} from '../../entities';

import type {Action} from '../../store/utils';
import type {ActionsObservable} from 'redux-observable';
import type {
  SubscribeRequest,
  SubscribeUpdateRequest,
  PurchaseSummaryRequest,
} from './api';
import {buildRoute} from '../../http';
import {GetSession} from '../auth/duck';

export type CardProfile = {
  id: string,
  current: boolean,
  displayName: string,
  firstName: string,
  lastName: string,
  idNumber: string,
  paymentProviderId: string,
  brand: string,
  type: string,
};
export type SubscriptionAcquisitionAction =
  | 'NEW_SUBSCRIPTION'
  | 'APPORTIONING'
  | 'UPDATE_SETTING'
  | 'FREE_TRIAL'
  | 'CHARGE_SUBSCRIPTION'
  | 'UPDATE_AND_CHARGE_SUBSCRIPTION';
export type CheckoutSummary = {
  acquisitionAction?: SubscriptionAcquisitionAction,
  discount: string,
  subtotal: string,
  total: string,
  credit: string,
  // local added attribute
  coupon?: string,
};

export type CheckoutAction = Action & {
  providersQuery?: {country?: string, currency?: string},
  // createCardRequest?: Object,
  purchaseSummaryRequest?: PurchaseSummaryRequest,
  subscribeRequest?: SubscribeRequest,
  subscribeUpdateRequest?: SubscribeUpdateRequest,
  // freeTrialSubscribeRequest?: {currency: string},
  freeTrialSubscribeRequest?: {
    storeTierId: string,
    frequency: 'MONTHLY' | 'YEARLY' | 'QUARTERLY' | 'SEMIANNUALLY',
  },
  setCurrentCardRequest?: {cardProfileId: string},
  cardProfiles?: CardProfile[],
  cardId?: string,
  invoiceId?: string,
  error?: string,
};

export type PayUInitialization = {
  subscriptionId: string,
  signature: string,
};

export type StripeInitialization = {
  subscriptionId?: string,
  clientSecret: string,
  userCardProfileId?: string,
  url?: string,
};

export type CheckoutState = {
  isSaving?: boolean,
  saveCardError?: string,
  paymentProviders?: PaymentProvider[],
  fetchPaymentProvidersError?: string,
  cardProfiles?: CardProfile[],
  isFetchingCardProfiles?: boolean,
  isFetching?: boolean,
  fetchCardsError?: string,
  deleteCardError?: string,
  checkoutSummary?: CheckoutSummary,
  fetchPurchaseSummaryError?: string,
  isPerformingCheckout?: boolean,
  performCheckoutSuccess?: boolean,
  performCheckoutError?: string,
  isSubscribing?: boolean,
  subscribeSuccess?: boolean,
  subscribeError?: boolean,
  payUInitialization?: PayUInitialization,
  stripeInitialization?: StripeInitialization,
  isInitializing?: boolean,
  cardState?: {message: string, state: string},
  isSettingCurrentCard?: boolean,
  setCurrentCardError?: string,
  freeTrialSubscribeSuccess?: boolean,
};

export class FetchPaymentProviders extends ActionCreator<CheckoutAction> {}
export class FetchPaymentProvidersSuccess extends ActionCreator<CheckoutAction> {}
export class FetchPaymentProvidersErrorFn extends ActionCreator<CheckoutAction> {}
export class DeleteCard extends ActionCreator<CheckoutAction> {}
export class DeleteCardSuccess extends ActionCreator<CheckoutAction> {}
export class DeleteCardErrorFn extends ActionCreator<CheckoutAction> {}
export class FetchCard extends ActionCreator<CheckoutAction> {}
export class FetchCardSuccess extends ActionCreator<CheckoutAction> {}
export class FetchCardErrorFn extends ActionCreator<CheckoutAction> {}
export class FetchCards extends ActionCreator<CheckoutAction> {}
export class FetchCardsSuccess extends ActionCreator<CheckoutAction> {}
export class FetchCardsErrorFn extends ActionCreator<CheckoutAction> {}
export class FetchPurchaseSummary extends ActionCreator<CheckoutAction> {}
export class FetchPurchaseSummarySuccess extends ActionCreator<CheckoutAction> {}
export class FetchPurchaseSummaryErrorFn extends ActionCreator<CheckoutAction> {}
export class PerformSubscribe extends ActionCreator<CheckoutAction> {}
export class PerformSubscribeSuccess extends ActionCreator<CheckoutAction> {}
export class PerformSubscribeErrorFn extends ActionCreator<CheckoutAction> {}
export class PayUInitialize extends ActionCreator<CheckoutAction> {}
export class PayUInitializeSuccess extends ActionCreator<CheckoutAction> {}
export class PayUInitializeErrorFn extends ActionCreator<CheckoutAction> {}
export class StripeInitialize extends ActionCreator<CheckoutAction> {}
export class StripeInitializeSuccess extends ActionCreator<CheckoutAction> {}
export class StripeInitializeErrorFn extends ActionCreator<CheckoutAction> {}
export class ResetStripeInitialization extends ActionCreator<CheckoutAction> {}
export class StripeUpdate extends ActionCreator<CheckoutAction> {}
export class StripeUpdateSuccess extends ActionCreator<CheckoutAction> {}
export class StripeUpdateErrorFn extends ActionCreator<CheckoutAction> {}
export class StripeAddCardInitialize extends ActionCreator<CheckoutAction> {}
export class StripeAddCardInitializeSuccess extends ActionCreator<CheckoutAction> {}
export class StripeAddCardInitializeErrorFn extends ActionCreator<CheckoutAction> {}
export class FreeTrialSubscribe extends ActionCreator<CheckoutAction> {}
export class FreeTrialSubscribeSuccess extends ActionCreator<CheckoutAction> {}
export class FreeTrialSubscribeErrorFn extends ActionCreator<CheckoutAction> {}

export class CheckCardState extends ActionCreator<CheckoutAction> {}
export class CheckCardStateSuccess extends ActionCreator<CheckoutAction> {}
export class CheckCardStateErrorFn extends ActionCreator<CheckoutAction> {}

export class SetCurrentCard extends ActionCreator<CheckoutAction> {}
export class SetCurrentCardSuccess extends ActionCreator<CheckoutAction> {}
export class SetCurrentCardErrorFn extends ActionCreator<CheckoutAction> {}

export class CheckoutResetState extends ActionCreator<CheckoutAction> {}

export const actions = {
  fetchPaymentProviders: (payload: CheckoutAction) =>
    new FetchPaymentProviders(payload),
  fetchCards: () => new FetchCards(),
  deleteCard: (payload: CheckoutAction) => new DeleteCard(payload),
  fetchPurchaseSummary: (payload: CheckoutAction) =>
    new FetchPurchaseSummary(payload),
  performSubscribe: (payload: CheckoutAction) => new PerformSubscribe(payload),
  freeTrialSubscribe: (payload: CheckoutAction) =>
    new FreeTrialSubscribe(payload),
  payUInitialize: (payload: CheckoutAction) => new PayUInitialize(payload),
  stripeInitialize: (payload: CheckoutAction) => new StripeInitialize(payload),
  resetStripeInitialization: (payload: CheckoutAction) =>
    new ResetStripeInitialization(payload),
  stripeUpdate: (payload: CheckoutAction) => new StripeUpdate(payload),
  stripeAddCardInitialize: (payload: CheckoutAction) =>
    new StripeAddCardInitialize(payload),
  checkCardState: (payload: CheckoutAction) => new CheckCardState(payload),
  checkoutResetState: () => new CheckoutResetState(),
  setCurrentCard: (payload: CheckoutAction) => new SetCurrentCard(payload),
};
export const initialState: CheckoutState = {};

export default createReducer<CheckoutState>(initialState, {
  [FetchPaymentProviders.type]: state => ({
    ...state,
    isFetching: true,
    fetchPaymentProvidersError: undefined,
  }),
  [FetchPaymentProvidersSuccess.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    paymentProviders: action.paymentProviders,
    isFetching: false,
    fetchPaymentProvidersError: undefined,
  }),
  [FetchPaymentProvidersErrorFn.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    isFetching: false,
    fetchPaymentProvidersError:
      'Error al intentar obtener los proveedores de pago',
  }),
  [DeleteCard.type]: state => ({
    ...state,
    isSaving: true,
    deleteCardError: undefined,
  }),
  [DeleteCardSuccess.type]: (state: CheckoutState, action: CheckoutAction) => ({
    ...state,
    isSaving: undefined,
  }),
  [DeleteCardErrorFn.type]: (state: CheckoutState, action: CheckoutAction) => ({
    ...state,
    isSaving: false,
    deleteCardError: action.error,
  }),
  [FetchCards.type]: state => ({
    ...state,
    isFetchingCardProfiles: true,
    fetchCardsError: undefined,
  }),
  [FetchCardsSuccess.type]: (state: CheckoutState, action: CheckoutAction) => ({
    ...state,
    cardProfiles: action.cardProfiles,
    isFetchingCardProfiles: undefined,
  }),
  [FetchCardsErrorFn.type]: (state: CheckoutState, action: CheckoutAction) => ({
    ...state,
    cardProfiles: [],
    isFetchingCardProfiles: undefined,
    fetchCardsError: action.error,
  }),
  [FetchPurchaseSummary.type]: state => ({
    ...state,
    isFetching: true,
    fetchPurchaseSummaryError: undefined,
  }),
  [FetchPurchaseSummarySuccess.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    fetchPurchaseSummaryError: undefined,
    checkoutSummary: action.purchaseSummary,
    isFetching: false,
  }),
  [FetchPurchaseSummaryErrorFn.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => {
    const couponError = action.error === 'Coupon not found';
    const checkoutSummary = state.checkoutSummary || {};
    if (!couponError) checkoutSummary.acquisitionAction = undefined;
    return {
      ...state,
      isFetching: false,
      checkoutSummary,
      fetchPurchaseSummaryError: action.error,
    };
  },
  [PerformSubscribe.type]: state => ({
    ...state,
    isSubscribing: true,
    subscribeSuccess: undefined,
    subscribeError: undefined,
  }),
  [PerformSubscribeSuccess.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    isSubscribing: false,
    subscribeSuccess: true,
    // invoice: action.invoice,
    // order: action.order,
  }),
  [PerformSubscribeErrorFn.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    isSubscribing: false,
    subscribeError: action.error,
  }),
  [PayUInitialize.type]: state => ({
    ...state,
    payUInitialization: undefined,
  }),
  [PayUInitializeSuccess.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    payUInitialization: action.data,
  }),
  [PayUInitializeErrorFn.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    payUInitializationError: true,
  }),
  [StripeInitialize.type]: state => ({
    ...state,
    stripeInitialization: undefined,
    isInitializing: true,
  }),
  [StripeInitializeSuccess.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    stripeInitialization: action.data,
    isInitializing: undefined,
  }),
  [StripeInitializeErrorFn.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    stripeInitializationError: true,
    isInitializing: undefined,
  }),
  [ResetStripeInitialization.type]: (state: CheckoutState) => ({
    ...state,
    stripeInitialization: undefined,
    isInitializing: undefined,
    stripeInitializationError: undefined,
  }),
  [StripeUpdate.type]: state => ({
    ...state,
    // stripeInitialization: undefined,
    isInitializing: true,
  }),
  [StripeUpdateSuccess.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    // stripeInitialization: action.data,
    isInitializing: undefined,
  }),
  [StripeUpdateErrorFn.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    stripeInitializationError: true,
    isInitializing: undefined,
  }),
  [StripeAddCardInitialize.type]: state => ({
    ...state,
    stripeInitialization: undefined,
    isInitializing: true,
  }),
  [StripeAddCardInitializeSuccess.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    stripeInitialization: action.data,
    isInitializing: undefined,
  }),
  [StripeAddCardInitializeErrorFn.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    stripeInitializationError: true,
    isInitializing: undefined,
  }),
  [FreeTrialSubscribe.type]: state => ({
    ...state,
    // isSubscribing: true,
    // subscribeSuccess: undefined,
    // subscribeError: undefined,
  }),
  [FreeTrialSubscribeSuccess.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    freeTrialSubscribeSuccess: true,
    // isSubscribing: false,
    // subscribeSuccess: true,
    // invoice: action.invoice,
    // order: action.order,
  }),
  [FreeTrialSubscribeErrorFn.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    // isSubscribing: false,
    // subscribeError: action.error,
  }),
  [CheckCardState.type]: state => ({
    ...state,
    isFetchingCardState: true,
    fetchCardStateError: undefined,
  }),
  [CheckCardStateSuccess.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    cardState: action.data,
    isFetchingCardState: undefined,
  }),
  [CheckCardStateErrorFn.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    isFetchingCardState: undefined,
    fetchCardStateError: action.error,
  }),
  [SetCurrentCard.type]: state => ({
    ...state,
    isSettingCurrentCard: true,
    setCurrentCardError: undefined,
  }),
  [SetCurrentCardSuccess.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    // cardState: action.data,
    isSettingCurrentCard: undefined,
  }),
  [SetCurrentCardErrorFn.type]: (
    state: CheckoutState,
    action: CheckoutAction
  ) => ({
    ...state,
    isSettingCurrentCard: undefined,
    setCurrentCardError: action.error,
  }),
  [CheckoutResetState.type]: (state: CheckoutState) => ({
    ...initialState,
    paymentProviders: state.paymentProviders,
  }),
});

export const fetchPaymentProvidersEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchPaymentProviders.type),
    switchMap(({providersQuery}: CheckoutAction) =>
      getPaymentProvidersApi(providersQuery).pipe(
        map(data => new FetchPaymentProvidersSuccess({paymentProviders: data})),
        buildCatchError(FetchPaymentProvidersErrorFn)
      )
    )
  );

export const deleteCardEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(DeleteCard.type),
    switchMap(({cardId}: CheckoutAction) =>
      deleteCardApi({
        route: buildRoute(endpoints.DELETE_CARD_PROFILE, [cardId]),
      }).pipe(
        map(card => new DeleteCardSuccess({card})),
        buildCatchError(DeleteCardErrorFn)
      )
    )
  );

export const fetchCardEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchCard.type),
    switchMap(() =>
      fetchCardApi({}).pipe(
        map(() => new FetchCardSuccess({})),
        buildCatchError(FetchCardErrorFn)
      )
    )
  );

export const fetchCardsEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchCards.type, DeleteCardSuccess.type, SetCurrentCardSuccess.type),
    debounceTime(100),
    switchMap(() =>
      fetchCardsApi().pipe(
        map(data => new FetchCardsSuccess({cardProfiles: data})),
        buildCatchError(FetchCardsErrorFn)
      )
    )
  );

export const fetchPurchaseSummaryEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchPurchaseSummary.type),
    switchMap(({purchaseSummaryRequest}: CheckoutAction) =>
      fetchPurchaseSummaryApi(purchaseSummaryRequest).pipe(
        map(
          data =>
            new FetchPurchaseSummarySuccess({
              purchaseSummary: {...data, coupon: purchaseSummaryRequest.coupon},
            })
        ),
        buildCatchError(FetchPurchaseSummaryErrorFn)
      )
    )
  );

export const performSubscribeEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(PerformSubscribe.type),
    switchMap(({subscribeRequest}: CheckoutAction) =>
      performSubscribeApi(subscribeRequest).pipe(
        map(
          nextSubscription =>
            new PerformSubscribeSuccess({data: nextSubscription})
        ),
        buildCatchError(PerformSubscribeErrorFn)
      )
    )
  );

export const payUInitializeEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(PayUInitialize.type),
    switchMap(({subscribeRequest}: CheckoutAction) =>
      payUInitializeApi(subscribeRequest).pipe(
        map(data => new PayUInitializeSuccess({data})),
        buildCatchError(PayUInitializeErrorFn)
      )
    )
  );

export const stripeInitializeEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(StripeInitialize.type),
    switchMap(({subscribeRequest}: CheckoutAction) =>
      stripeInitializeApi(subscribeRequest).pipe(
        map(data => new StripeInitializeSuccess({data})),
        buildCatchError(StripeInitializeErrorFn)
      )
    )
  );

export const stripeAddCardInitializeEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(StripeAddCardInitialize.type),
    switchMap(({subscribeRequest}: CheckoutAction) =>
      stripeAddCardInitializeApi(subscribeRequest).pipe(
        map(data => new StripeAddCardInitializeSuccess({data})),
        buildCatchError(StripeAddCardInitializeErrorFn)
      )
    )
  );

export const stripeUpdateEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(StripeUpdate.type),
    switchMap(({subscribeUpdateRequest}: CheckoutAction) =>
      stripeUpdateApi(subscribeUpdateRequest).pipe(
        map(data => new StripeUpdateSuccess({data})),
        buildCatchError(StripeUpdateErrorFn)
      )
    )
  );

export const freeTrialSubscribeEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FreeTrialSubscribe.type),
    debounceTime(100),
    switchMap(({freeTrialSubscribeRequest}: CheckoutAction) =>
      freeTrialSubscribeApi(freeTrialSubscribeRequest).pipe(
        mergeMap(invoice =>
          of(new FreeTrialSubscribeSuccess({invoice}), new GetSession())
        ),
        buildCatchError(FreeTrialSubscribeErrorFn)
      )
    )
  );

export const checkCardStateEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(CheckCardState.type),
    switchMap(({checkCardStateRequest}: CheckoutAction) =>
      fetchCardStateApi(undefined, {
        route: buildRoute(endpoints.STRIPE_CARD_STATE, [
          checkCardStateRequest.cardProfileId,
        ]),
      }).pipe(
        map(data => new CheckCardStateSuccess({data})),
        buildCatchError(CheckCardStateErrorFn)
      )
    )
  );

export const setCurrentCardEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(SetCurrentCard.type),
    switchMap(({setCurrentCardRequest}: CheckoutAction) =>
      setCurrentCardApi(undefined, {
        route: buildRoute(endpoints.SET_CURRENT_CARD, [
          setCurrentCardRequest.cardProfileId,
        ]),
      }).pipe(
        map(data => new SetCurrentCardSuccess({data})),
        buildCatchError(SetCurrentCardErrorFn)
      )
    )
  );
